import { modalList, show } from '@/common/utils/ControlModal';
import { logSplashIntroIssue } from '@/common/utils/SplashIntroLogger';
import Util from '@/common/utils/Util';

const INTRO_RELOAD_MODAL_TEXT = '서버 연결이 원활하지 않습니다. 앱을 다시 실행해 주세요.';
const INTRO_RELOAD_MODAL_CONFIRM_TEXT = '확인';
const LOCATION_PENDING_REASON = 'location';
const LOCATION_PENDING_MAX_DELAY = 30000;

class SplashIntroManager {
  constructor() {
    this.fallbackTimers = {};
    this.fallbackOptions = {};
    this.pauseReasons = new Set();
    this.locationPendingTimer = null;
    this.hasShownReloadModal = false;
    this.pendingHideKey = null;
    this.pendingReloadModal = null;
  }

  isPaused() {
    return this.pauseReasons.size > 0;
  }

  scheduleFallback(key = '', options = {}) {
    if (!key) return;
    this.fallbackOptions[key] = options;

    const { delayMs = 0, canRun = () => true } = options;
    if (!Util.isApp() || this.fallbackTimers[key] || Util.isNativeIntroHidden() || this.isPaused() || !canRun()) return;

    this.fallbackTimers[key] = window.setTimeout(() => {
      this.fallbackTimers[key] = null;
      if (this.isPaused() || Util.isNativeIntroHidden() || !canRun()) return;
      this.showReloadModal(options);
    }, delayMs);
  }

  clearFallback(key = '') {
    if (!key || !this.fallbackTimers[key]) return;
    window.clearTimeout(this.fallbackTimers[key]);
    this.fallbackTimers[key] = null;
  }

  clearAllFallbacks() {
    Object.keys(this.fallbackTimers).forEach((key) => this.clearFallback(key));
  }

  pause(reason = '') {
    if (!reason) return;
    this.pauseReasons.add(reason);
    this.clearAllFallbacks();
  }

  resume(reason = '') {
    if (!reason) return;
    this.pauseReasons.delete(reason);
    if (this.isPaused()) return;
    if (this.pendingHideKey !== null) {
      const key = this.pendingHideKey;
      this.pendingHideKey = null;
      this.pendingReloadModal = null;
      this.hideIntro(key);
      return;
    }
    if (this.showPendingReloadModal()) return;
    Object.keys(this.fallbackOptions).forEach((key) => this.scheduleFallback(key, this.fallbackOptions[key]));
  }

  startLocationPending() {
    this.pause(LOCATION_PENDING_REASON);
    if (this.locationPendingTimer) window.clearTimeout(this.locationPendingTimer);
    this.locationPendingTimer = window.setTimeout(() => {
      this.clearLocationPending();
    }, LOCATION_PENDING_MAX_DELAY);
  }

  clearLocationPending() {
    if (this.locationPendingTimer) {
      window.clearTimeout(this.locationPendingTimer);
      this.locationPendingTimer = null;
    }
    this.resume(LOCATION_PENDING_REASON);
  }

  hideIntro(key = '') {
    if (key) this.clearFallback(key);
    if (this.isPaused()) {
      this.pendingHideKey = key;
      return false;
    }
    return Util.nativeHideIntroOnce();
  }

  requestReloadModal(options = {}, shouldShow = () => true) {
    if (!shouldShow()) return false;
    if (this.isPaused()) {
      this.pendingReloadModal = { options, shouldShow };
      return false;
    }
    return this.showReloadModal(options);
  }

  showPendingReloadModal() {
    if (!this.pendingReloadModal) return false;
    const { options, shouldShow } = this.pendingReloadModal;
    this.pendingReloadModal = null;
    if (!shouldShow()) return false;
    return this.showReloadModal(options);
  }

  resetForReload() {
    this.clearAllFallbacks();
    if (this.locationPendingTimer) {
      window.clearTimeout(this.locationPendingTimer);
      this.locationPendingTimer = null;
    }
    this.pauseReasons.clear();
    this.pendingHideKey = null;
    this.pendingReloadModal = null;
    this.hasShownReloadModal = false;
    Util.resetNativeIntroState();
  }

  reloadApp() {
    this.resetForReload();
    Util.nativeReloadAndReplaceRoot();
  }

  showReloadModal({ reason = '', getContext = () => ({}), force = false } = {}) {
    if (this.hasShownReloadModal || (!force && Util.isNativeIntroHidden())) return false;
    this.hasShownReloadModal = true;
    logSplashIntroIssue(reason, getContext());
    show(modalList.Alert, {
      text: INTRO_RELOAD_MODAL_TEXT,
      confirmText: INTRO_RELOAD_MODAL_CONFIRM_TEXT,
      confirmCallback: this.reloadApp.bind(this)
    });
    Util.nativeHideIntroOnce();
    return true;
  }
}

export default new SplashIntroManager();
